import { useState, useMemo } from 'react';
import { RefreshCw, Search, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useTradingAudit } from '../../api/hooks';
import { SectionHeader, DataTable, ErrorState } from '../../components/ui';
import { fmtDateTime, cn } from '../../lib/utils';

const STATUS_OPTIONS = ['ALL', 'SUCCESS', 'FAILED', 'PENDING', 'TIMEOUT'];

const STATUS_STYLE = {
  SUCCESS: 'bg-bull/10 border-bull/30 text-bull',
  FAILED:  'bg-bear/10 border-bear/30 text-bear',
  PENDING: 'bg-warn/10 border-warn/30 text-warn',
  TIMEOUT: 'bg-bg-hover border-bg-border text-txt-muted',
};

function getTodayIstDate() {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(new Date());
}

export default function AdminTradingAudit() {
  const todayIst = getTodayIstDate();
  const [searchText, setSearchText] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [fromDate, setFromDate] = useState(todayIst);
  const [toDate, setToDate] = useState(todayIst);
  const [appliedRange, setAppliedRange] = useState({ from: todayIst, to: todayIst });

  const queryParams = useMemo(() => ({
    limit: 500,
    offset: 0,
    from_date: appliedRange.from || todayIst,
    to_date: appliedRange.to || todayIst,
    ...(appliedSearch.trim() ? { search: appliedSearch.trim() } : {}),
    ...(statusFilter !== 'ALL' ? { status_filter: statusFilter } : {}),
  }), [appliedRange, appliedSearch, statusFilter, todayIst]);

  const { data, isLoading, error, refetch } = useTradingAudit(queryParams);
  const items = data?.items || [];

  const applyDates = () => {
    const start = fromDate || todayIst;
    const end = toDate || todayIst;
    if (start > end) return;
    setAppliedRange({ from: start, to: end });
  };

  const resetDates = () => {
    setFromDate(todayIst);
    setToDate(todayIst);
    setAppliedRange({ from: todayIst, to: todayIst });
  };

  const columns = [
    {
      key: 'order_time',
      label: 'Order Time',
      width: '155px',
      render: (v) => <span className="font-mono text-txt-muted whitespace-nowrap">{fmtDateTime(v)}</span>,
    },
    {
      key: 'user_id',
      label: 'User',
      width: '80px',
      render: (v) => <span className="font-mono text-txt-muted">#{v}</span>,
    },
    {
      key: 'email',
      label: 'Email',
      width: '180px',
      render: (v) => <span className="text-txt-secondary text-2xs truncate block max-w-[180px]">{v || '-'}</span>,
    },
    {
      key: 'user_name',
      label: 'Name',
      width: '120px',
      render: (v) => <span className="text-txt-secondary truncate block max-w-[120px]">{v || '-'}</span>,
    },
    {
      key: 'deployment_id',
      label: 'Dep',
      width: '65px',
      render: (v) => <span className="font-mono text-txt-muted">{v ? `#${v}` : '-'}</span>,
    },
    {
      key: 'strategy_name',
      label: 'Strategy',
      width: '130px',
      render: (v) => <span className="text-txt-secondary truncate block max-w-[130px]">{v || '-'}</span>,
    },
    {
      key: 'symbol',
      label: 'Symbol',
      width: '140px',
      render: (v) => <span className="font-mono text-txt-primary text-2xs">{v || '-'}</span>,
    },
    {
      key: 'side',
      label: 'Side',
      width: '55px',
      render: (v) => v ? (
        <span className={cn('badge border text-2xs',
          v === 'BUY' ? 'bg-bull/10 border-bull/30 text-bull' : 'bg-bear/10 border-bear/30 text-bear'
        )}>{v}</span>
      ) : '-',
    },
    {
      key: 'qty',
      label: 'Qty',
      width: '55px',
      render: (v) => <span className="font-mono text-txt-secondary">{v ?? '-'}</span>,
    },
    {
      key: 'price',
      label: 'Price',
      width: '75px',
      render: (v) => <span className="font-mono text-txt-primary">{v != null ? `${Number(v).toFixed(2)}` : '-'}</span>,
    },
    {
      key: 'broker_name',
      label: 'Broker',
      width: '80px',
      render: (v) => <span className="font-mono text-txt-muted text-2xs">{v || '-'}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      width: '100px',
      render: (v, row) => (
        <span
          className={cn('badge border text-2xs inline-flex items-center gap-1', STATUS_STYLE[v] || 'bg-bg-hover border-bg-border text-txt-muted')}
          title={row.error_msg || ''}
        >
          {v === 'SUCCESS' ? <CheckCircle size={9} /> : v === 'FAILED' ? <XCircle size={9} /> : <Clock size={9} />}
          {v}
        </span>
      ),
    },
    {
      key: 'error_msg',
      label: 'Error / Reason',
      width: '250px',
      render: (v) => <span className="font-mono text-bear text-2xs whitespace-normal">{v || '-'}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Trading Audit"
        subtitle="Broker order log across all users"
      />

      {/* Filter bar */}
      <div className="panel p-4 flex items-center gap-3 flex-wrap">
        <Search size={13} className="text-amber shrink-0" />
        <input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setAppliedSearch(searchText)}
          placeholder="Search user, email, symbol, strategy..."
          className="input-base w-72 text-xs"
        />
        <button onClick={() => setAppliedSearch(searchText)} className="btn-primary text-xs flex items-center gap-1.5 py-1.5">
          <Search size={11} /> Search
        </button>
        {appliedSearch && (
          <button onClick={() => { setSearchText(''); setAppliedSearch(''); }} className="btn-ghost text-xs py-1.5">
            Clear
          </button>
        )}
        <span className="text-xs text-txt-muted font-mono ml-2">From:</span>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="input-base w-40 text-xs" />
        <span className="text-xs text-txt-muted font-mono">To:</span>
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="input-base w-40 text-xs" />
        <button onClick={applyDates} disabled={!fromDate || !toDate || fromDate > toDate} className="btn-primary text-xs py-1.5">
          Apply
        </button>
        <button onClick={resetDates} className="btn-ghost text-xs py-1.5">Reset</button>
        <button onClick={refetch} className="btn-ghost text-xs flex items-center gap-1.5 py-1.5 ml-auto">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      {/* Status pills */}
      <div className="flex items-center gap-2 flex-wrap">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={cn(
              'px-2 py-1 rounded text-2xs font-mono transition-colors border',
              statusFilter === s
                ? 'bg-amber/10 border-amber/30 text-amber'
                : 'border-transparent text-txt-muted hover:text-txt-secondary',
            )}
          >
            {s}
          </button>
        ))}
        <span className="text-2xs font-mono text-txt-muted ml-auto">
          {items.length} orders &bull; {appliedRange.from} to {appliedRange.to} (IST)
        </span>
      </div>

      {/* Table */}
      <div className="panel p-4 overflow-x-auto">
        <div className="min-w-[1500px]">
          {error ? (
            <ErrorState message={error.message || 'Failed to load trading audit'} onRetry={refetch} />
          ) : (
            <DataTable columns={columns} data={items} loading={isLoading} emptyText="No broker orders found" />
          )}
        </div>
      </div>
    </div>
  );
}
